import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, IconButton, CircularProgress, Alert, TextField, InputAdornment, Tooltip } from '@mui/material';
import { ArrowLeft, Plus, Pencil, Trash2, Search, MapPin } from 'lucide-react';
import { LocationType } from '../../types/settingsLocationTypeModel';
import { getAllLocationTypes } from '../../services/settingsLocationType.api';
import DeleteLocationTypeModal from '../../components/admin/DeleteLocationTypeModal';
import LocationTypeFormModal from '../../components/admin/LocationTypeFormModal';
// import LocationTypeFormModal from './components/LocationTypeFormModal';
// import DeleteLocationTypeModal from './components/DeleteLocationTypeModal';

// NOTE: PageHeader / Card / inputSx are assumed to be shared admin UI primitives
// already used across AdminReportsSettings.tsx. Re-declared locally here in a
// lightweight form so this file has no external dependency beyond MUI + lucide.

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div
    style={{
      background: '#fff',
      border: '1px solid var(--border)',
      borderRadius: 16,
      padding: 22,
      boxShadow: '0 1px 2px rgba(30,20,10,0.04)',
      ...style,
    }}
  >
    {children}
  </div>
);

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: 13,
    background: '#fff',
    '& fieldset': { borderColor: 'var(--border)' },
    '&:hover fieldset': { borderColor: 'var(--brand)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--brand)', borderWidth: 1.5 },
  },
};

const trafficColor = (level: string) => {
  const l = level.toLowerCase();
  if (l.includes('very high')) return { bg: '#fee2e2', fg: '#dc2626' };
  if (l.includes('high')) return { bg: '#ffedd5', fg: '#ea580c' };
  if (l.includes('medium to high')) return { bg: '#fef3c7', fg: '#d97706' };
  if (l.includes('medium')) return { bg: '#fef9c3', fg: '#ca8a04' };
  if (l.includes('low to medium')) return { bg: '#dcfce7', fg: '#16a34a' };
  return { bg: '#e0e7ff', fg: '#4f46e5' };
};

const LocationTypeMaster: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<LocationType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<LocationType | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<LocationType | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getAllLocationTypes();
      setItems(result);
    } catch (e: any) {
      setError(e.message || 'Failed to load location types.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(
      i =>
        i.name.toLowerCase().includes(q) ||
        i.trafficLevel.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q)
    );
  }, [items, search]);

  const openCreate = () => { setEditItem(null); setModalOpen(true); };
  const openEdit = (item: LocationType) => { setEditItem(item); setModalOpen(true); };
  const openDelete = (item: LocationType) => { setDeleteItem(item); setDeleteOpen(true); };

  return (
    <div style={{ background: 'var(--bg-base)', minHeight: '100vh', padding: '28px 32px' }} className="page-enter">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <IconButton
          onClick={() => navigate('/admin/settings')}
          sx={{
            border: '1px solid var(--border)',
            borderRadius: '10px',
            width: 38,
            height: 38,
            color: 'var(--text-secondary)',
          }}
        >
          <ArrowLeft size={17} />
        </IconButton>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h2 style={{ margin: 0, fontFamily: 'Playfair Display, serif', fontWeight: 700, fontSize: 22, color: 'var(--text-primary)' }}>
            Location Type Master
          </h2>
          <p style={{ margin: '2px 0 0', fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--text-muted)' }}>
            Manage location types, traffic levels and commission rates
          </p>
        </div>
        <button onClick={openCreate} className="btn-brand" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', fontSize: 13 }}>
          <Plus size={16} /> Add Location Type
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 18, maxWidth: 360 }}>
        <TextField
          placeholder="Search location types..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          fullWidth
          size="small"
          sx={inputSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={15} color="var(--text-muted)" />
              </InputAdornment>
            ),
          }}
        />
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, fontFamily: 'DM Sans', fontSize: 13, borderRadius: '10px' }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
          <CircularProgress size={28} sx={{ color: 'var(--brand)' }} />
        </div>
      ) : filtered.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
          <MapPin size={32} color="var(--text-muted)" style={{ marginBottom: 10 }} />
          <p style={{ fontFamily: 'DM Sans', fontSize: 14, color: 'var(--text-muted)', margin: 0 }}>
            {search ? 'No location types match your search.' : 'No location types added yet.'}
          </p>
        </Card>
      ) : (
        <Grid container spacing={2.5}>
          {filtered.map(item => {
            const tColor = trafficColor(item.trafficLevel);
            return (
              <Grid item xs={12} md={6} lg={4} key={item.id}>
                <Card style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{
                          background: 'var(--bg-muted)',
                          color: 'var(--text-muted)',
                          fontFamily: 'DM Sans',
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '2px 7px',
                          borderRadius: 6,
                        }}
                      >
                        #{item.sno}
                      </span>
                      <h3 style={{ margin: 0, fontFamily: 'DM Sans', fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                        {item.name}
                      </h3>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: 'var(--text-secondary)' }}>
                          <Pencil size={14} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => openDelete(item)} sx={{ color: '#dc2626' }}>
                          <Trash2 size={14} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  <p style={{ fontFamily: 'DM Sans', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 14px', flex: 1 }}>
                    {item.description}
                  </p>

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span
                      style={{
                        background: tColor.bg,
                        color: tColor.fg,
                        fontFamily: 'DM Sans',
                        fontWeight: 600,
                        fontSize: 11,
                        padding: '4px 10px',
                        borderRadius: 20,
                      }}
                    >
                      {item.trafficLevel} Traffic
                    </span>
                    <span
                      style={{
                        background: 'var(--brand-light)',
                        color: 'var(--brand)',
                        fontFamily: 'DM Sans',
                        fontWeight: 600,
                        fontSize: 11,
                        padding: '4px 10px',
                        borderRadius: 20,
                      }}
                    >
                      {item.commissionPercentage}% Commission
                    </span>
                  </div>

                  <div style={{ borderTop: '1px solid var(--border-light)', marginTop: 14, paddingTop: 10, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: 'DM Sans', fontSize: 10.5, color: 'var(--text-muted)' }}>Created {item.cdate}</span>
                    <span style={{ fontFamily: 'DM Sans', fontSize: 10.5, color: 'var(--text-muted)' }}>{item.ctime}</span>
                  </div>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <LocationTypeFormModal
        open={modalOpen}
        editItem={editItem}
        onClose={() => setModalOpen(false)}
        onSuccess={fetchData}
      />

      <DeleteLocationTypeModal
        open={deleteOpen}
        item={deleteItem}
        onClose={() => setDeleteOpen(false)}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default LocationTypeMaster;